import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
    Box,
    Card,
    Typography,
    Grid,
    Avatar,
    Button,
    styled,
    useTheme,
    useMediaQuery,
    Fab,
    Zoom,
    Tooltip,
    Stack,
    IconButton
} from '@mui/material';

/* ✅ ProDev Assets & Icons */
import { AiFillBulb, AiOutlineRead } from "react-icons/ai";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CodeIcon from "@mui/icons-material/Code";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import StopIcon from "@mui/icons-material/Stop";
import SettingsEthernetIcon from '@mui/icons-material/SettingsEthernet';

const bgImage = "https://images.unsplash.com/photo-1451187580459-43490279c0fa";

/* ✅ Styled Components - ProDev Dark Glass Theme */
const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(16,7,32,0.9), rgba(16,7,32,0.95)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    paddingBottom: "80px",
    paddingTop: theme.spacing(12),
    color: "white",
}));

const SectionCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(16,7,32,0.85)",
    borderRadius: "14px",
    padding: theme.spacing(3),
    marginBottom: "25px",
    boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
    border: "1px solid rgba(255,255,255,0.1)",
    color: "white",
}));

const CodeBlock = styled("pre")(({ theme }) => ({
    backgroundColor: "#0d1117",
    color: "#c9d1d9",
    padding: "20px",
    borderRadius: "10px",
    overflowX: "auto",
    fontSize: "0.9rem",
    lineHeight: 1.6,
    border: "1px solid rgba(255,255,255,0.05)",
    fontFamily: "'Fira Code', 'Courier New', monospace",
}));

const ActionButton = styled(Button)(({ theme }) => ({
    backgroundColor: "goldenrod",
    color: "#100720",
    fontWeight: 800,
    padding: "12px 40px",
    borderRadius: "999px",
    transition: "0.3s",
    "&:hover": { backgroundColor: "#e0b200", transform: "scale(1.05)" },
}));

/**
 * APIFetchingMethods Component
 * Specialized ProDevConsult Module
 * Author: Hazzan
 */
export default function APIFetchingMethods() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    /* ✅ State Management for Live Demos */
    const [fetchUser, setFetchUser] = useState(null);
    const [axiosUser, setAxiosUser] = useState(null);
    const [showScroll, setShowScroll] = useState(false);
    const [copiedIndex, setCopiedIndex] = useState(null);
    const [isSpeaking, setIsSpeaking] = useState(false);

    useEffect(() => {
        // Native Fetch Implementation
        fetch("https://jsonplaceholder.typicode.com/users/1")
            .then(res => res.json())
            .then(data => setFetchUser(data))
            .catch(err => console.error("ProDev Fetch Error:", err));

        // Axios Implementation
        axios.get("https://jsonplaceholder.typicode.com/users/2")
            .then(res => setAxiosUser(res.data))
            .catch(err => console.error("ProDev Axios Error:", err));

        const checkScroll = () => setShowScroll(window.pageYOffset > 400);
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, []);

    const handleSpeak = (text) => {
        window.speechSynthesis.cancel();
        if (isSpeaking) { setIsSpeaking(false); return; }
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.onstart = () => setIsSpeaking(true);
        utterance.onend = () => setIsSpeaking(false);
        window.speechSynthesis.speak(utterance);
    };

    const handleCopy = (text, index) => {
        navigator.clipboard.writeText(text);
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(null), 2000);
    };

    const apiModules = useMemo(() => [
        {
            title: "1. Native Fetch API",
            tag: "Standard HTTP",
            desc: "The standard fetch() method is built into modern browsers. It returns a promise that resolves into a Response object, which we then parse as JSON.",
            code: `fetch("https://api.prodev.com/user/1")\n  .then(response => response.json())\n  .then(data => console.log(data))\n  .catch(error => console.error(error));`
        },
        {
            title: "2. Axios Library",
            tag: "Feature-Rich Client",
            desc: "Axios is our preferred HTTP client at ProDevConsult. It handles JSON parsing automatically and offers better protection against XSRF.",
            code: `import axios from 'axios';\n\naxios.get("https://api.prodev.com/user/2")\n  .then(res => console.log(res.data))\n  .catch(err => console.error(err));`
        },
        {
            title: "3. Async / Await Pattern",
            tag: "Modern Syntax",
            desc: "This is the most elegant way to handle promises. It makes asynchronous code look and behave like synchronous code, improving readability.",
            code: `async function getProDevData() {\n  try {\n    const res = await fetch(url);\n    const data = await res.json();\n    return data;\n  } catch (err) {\n    console.error(err);\n  }\n}`
        },
        {
            title: "4. GraphQL Queries",
            tag: "Precise Fetching",
            desc: "Unlike REST, GraphQL allows Hazzan to request only the specific fields needed, reducing the data payload and improving performance.",
            code: `query {\n  user(id: "prodev_1") {\n    name\n    role\n    company\n  }\n}`
        },
        {
            title: "5. WebSockets",
            tag: "Real-Time Stream",
            desc: "For live data like stock prices or chats, we use WebSockets to maintain an open persistent connection for bidirectional communication.",
            code: `const socket = new WebSocket("wss://stream.prodev.com");\nsocket.onmessage = (event) => {\n  const data = JSON.parse(event.data);\n  console.log("Live Feed:", data);\n};`
        }
    ], []);

    return (
        <PageWrapper>
            <Box sx={{ textAlign: "center", mb: 6, px: 2 }}>
                <Typography variant={isMobile ? "h4" : "h3"} sx={{
                    fontWeight: 800, backgroundColor: "gold", color: "#100720",
                    px: { xs: 3, md: 6 }, py: 1.5, borderRadius: "999px",
                    display: "inline-block", boxShadow: "0 6px 20px rgba(218, 165, 32, 0.3)",
                }}>
                    PRODEV FETCHING ARCHITECTURE
                </Typography>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 } }}>
                <Grid item xs={12}>
                    <SectionCard>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                            <Typography variant="h5" fontWeight={800} color="goldenrod">
                                Overview: Data Retrieval Methods
                            </Typography>
                            <IconButton onClick={() => handleSpeak("API fetching is the process of requesting data from an external source. In this ProDevConsult module, Hazzan explains the six essential techniques for data handling.")} sx={{ color: "goldenrod" }}>
                                <VolumeUpIcon />
                            </IconButton>
                        </Stack>
                        <Typography variant="body1" sx={{ opacity: 0.9, lineHeight: 1.7 }}>
                            Connecting your UI to a data source is a fundamental skill. Whether using native <b>fetch</b>, the powerful <b>Axios</b> client, or real-time <b>WebSockets</b>, choosing the right tool ensures your application remains fast, secure, and reactive.
                        </Typography>
                    </SectionCard>
                </Grid>

                {apiModules.map((item, i) => (
                    <Grid item xs={12} key={i}>
                        <SectionCard>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 2 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                    <Avatar sx={{ bgcolor: "rgba(218, 165, 32, 0.2)", color: "goldenrod" }}><AiOutlineRead /></Avatar>
                                    <Box>
                                        <Typography variant="h6" fontWeight={700}>{item.title}</Typography>
                                        <Typography variant="caption" color="goldenrod" fontWeight={800}>{item.tag}</Typography>
                                    </Box>
                                </Box>
                                <Tooltip title={copiedIndex === i ? "Copied!" : "Copy Code"}>
                                    <IconButton onClick={() => handleCopy(item.code, i)} sx={{ color: "white" }}>
                                        {copiedIndex === i ? <CheckIcon /> : <ContentCopyIcon />}
                                    </IconButton>
                                </Tooltip>
                            </Box>

                            <Typography variant="body1" sx={{ mb: 2, opacity: 0.9 }}>{item.desc}</Typography>
                            <CodeBlock>{item.code}</CodeBlock>

                            {/* Live API Results */}
                            {i === 0 && <Typography variant="caption" sx={{ color: "goldenrod", mt: 1, display: "block" }}>Live Fetch: {fetchUser?.name || "Connecting..."}</Typography>}
                            {i === 1 && <Typography variant="caption" sx={{ color: "goldenrod", mt: 1, display: "block" }}>Live Axios: {axiosUser?.name || "Connecting..."}</Typography>}
                        </SectionCard>
                    </Grid>
                ))}

                <Grid item xs={12} textAlign="center" sx={{ mt: 4, mb: 10 }}>
                    <Link to="/javascriptcoding" style={{ textDecoration: "none" }}>
                        <ActionButton variant="contained">PROCEED TO SYSTEM INTEGRATION</ActionButton>
                    </Link>
                </Grid>
            </Grid>

            <Stack spacing={2} sx={{ position: "fixed", bottom: 25, right: 25, zIndex: 1000 }}>
                {isSpeaking && (
                    <Zoom in={isSpeaking}>
                        <Fab size="medium" color="error" onClick={() => window.speechSynthesis.cancel()}><StopIcon /></Fab>
                    </Zoom>
                )}
                <Zoom in={showScroll}>
                    <Fab size="small" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} sx={{ bgcolor: "goldenrod" }}>
                        <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                    </Fab>
                </Zoom>
            </Stack>
        </PageWrapper>
    );
}
